import React, { useState } from "react";
import { Loader2, Users, GitBranch } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Panel } from "@/components/common/Panel";
import { SeverityBadge } from "@/components/common/Badges";
import { TID } from "@/lib/testIds";
import { SuggestedControls } from "@/components/roles/SuggestedControls";
import { FunctionDrilldown } from "@/components/roles/FunctionDrilldown";
import { MermaidRenderer } from "@/components/roles/MermaidRenderer";

export function CrossRoleSimulationPanel({ selected, simulating, simulate, crossSod }) {
    const [diagram, setDiagram] = useState(null);
    const [diagramLoading, setDiagramLoading] = useState(false);

    const visualize = async () => {
        setDiagramLoading(true);
        try {
            const { data } = await api.post("/diagrams/cross-role/generate", { role_ids: [...selected] });
            setDiagram(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Diagram generation failed");
        } finally {
            setDiagramLoading(false);
        }
    };

    return (
        <Panel
            eyebrow="User Access Simulation"
            title="Cross-role Segregation-of-Duties"
            action={
                <div className="flex items-center gap-3">
                    {crossSod && (
                        <button
                            data-testid={TID.crossRoleDiagramBtn}
                            onClick={visualize}
                            disabled={diagramLoading}
                            className="inline-flex items-center gap-1.5 text-xs font-mono text-cyan-400 hover:text-cyan-300 transition-colors disabled:opacity-40"
                        >
                            {diagramLoading ? <Loader2 size={13} className="animate-spin" /> : <GitBranch size={13} />}
                            Visualize as Diagram
                        </button>
                    )}
                    <button
                        data-testid={TID.roleSimulateBtn}
                        onClick={simulate}
                        disabled={simulating || selected.size < 1}
                        className="inline-flex items-center gap-1.5 text-xs font-mono text-cyan-400 hover:text-cyan-300 transition-colors disabled:opacity-40"
                    >
                        {simulating ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />}
                        Simulate combined access ({selected.size})
                    </button>
                </div>
            }
        >
            <p className="text-xs text-slate-500 font-mono mb-3">
                Tick roles in the library above to model a user holding them together, then simulate to reveal
                toxic combinations that only emerge across multiple roles.
            </p>
            {!crossSod ? (
                <p className="text-sm text-slate-500 font-mono py-4 text-center">
                    No simulation run yet — select roles and click “Simulate combined access”.
                </p>
            ) : (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
                        <span className="text-slate-400">Simulated user roles:</span>
                        {crossSod.roles.map((n, i) => (
                            <span key={`${n}-${i}`} className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-200 border border-white/10">{n}</span>
                        ))}
                        <span className="text-slate-500">· {crossSod.combined_tcode_count} combined tcodes</span>
                        <SeverityBadge severity={crossSod.highest_risk} />
                    </div>
                    {crossSod.conflicts.length === 0 ? (
                        <p className="text-sm text-emerald-400/90 font-mono py-2">
                            No SoD conflicts in the combined access set — this role assignment is clean.
                        </p>
                    ) : (
                        crossSod.conflicts.map((c) => (
                            <div key={c.rule_id} className={`panel-elevated p-3 border-l-2 ${c.cross_role ? "border-rose-500/60" : "border-amber-500/40"}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-[10px] text-rose-300">{c.rule_id}</span>
                                    <SeverityBadge severity={c.severity} />
                                    <span className="text-xs text-white font-medium">{c.rule_name}</span>
                                    {c.cross_role ? (
                                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-rose-500/15 text-rose-300 border border-rose-500/30">CROSS-ROLE</span>
                                    ) : (
                                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30">WITHIN-ROLE</span>
                                    )}
                                    {(c.suggested_controls || []).length > 0 && (
                                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" title="Mitigation proposals below">
                                            {c.suggested_controls.map((s) => s.control_id).join(" · ")}
                                        </span>
                                    )}
                                </div>
                                <p className="text-[11px] text-slate-400 mt-1">{c.description}</p>
                                <div className="text-[10px] font-mono text-slate-500 mt-1.5">
                                    <span className="text-cyan-400/80">{c.function_a}</span> [{c.matched_a.join(", ")}] via {c.roles_a.join(", ")}
                                </div>
                                <div className="text-[10px] font-mono text-slate-500">
                                    <span className="text-cyan-400/80">{c.function_b}</span> [{c.matched_b.join(", ")}] via {c.roles_b.join(", ")}
                                </div>
                                <FunctionDrilldown
                                    testId={`cross-conflict-drilldown-${c.rule_id}`}
                                    functions={[
                                        { name: c.function_a, matched: c.matched_a },
                                        { name: c.function_b, matched: c.matched_b },
                                    ]}
                                />
                                <SuggestedControls controls={c.suggested_controls} />
                            </div>
                        ))
                    )}
                    {crossSod.critical_access?.length > 0 && (
                        <div className="space-y-2 pt-2">
                            <div className="label-eyebrow text-orange-400">Standalone Critical Activities in Combined Set</div>
                            {crossSod.critical_access.map((c) => (
                                <div key={c.risk_id} className="panel-elevated p-3 border-l-2 border-orange-500/50">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-[10px] text-orange-300">{c.risk_id}</span>
                                        <SeverityBadge severity={c.severity} />
                                        <span className="text-xs text-white font-medium">{c.name}</span>
                                    </div>
                                    <div className="text-[10px] font-mono text-slate-500 mt-1">{c.function} [{c.matched.join(", ")}]</div>
                                    <FunctionDrilldown
                                        testId={`cross-critical-drilldown-${c.risk_id}`}
                                        functions={[{ name: c.function, matched: c.matched }]}
                                    />
                                    <SuggestedControls controls={c.suggested_controls} />
                                </div>
                            ))}
                        </div>
                    )}
                    {diagram && <MermaidRenderer code={diagram.mermaid_code} title={diagram.title} />}
                </div>
            )}
        </Panel>
    );
}
